import { useCallback, useMemo } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useLocalization } from "@/context/localization-context";
import { translateOptional } from "@/i18n/translate";
import { cn } from "@/lib/utils";
import { EmptyBlock, Meter, SectionCard } from "../../components/primitives";
import type { ChartSeries } from "../../components/time-series-chart";
import { formatCount, formatMs, formatMsTick, formatPercent } from "../../format";
import { NEUTRAL, SERIES, SERIES_LIGHT, STATUS } from "../../palette";
import type { HistSummary, MetricSeries, PipelineSection } from "../../types";
import { counterTotal, counterValues, histTotal, histValues } from "./helpers";
import { MetricChartCard, SeriesTotals } from "./shared";
import { stepBreakdown } from "./step-layout";

const STEP_GRID =
  "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 md:grid-cols-[minmax(10rem,2fr)_minmax(4rem,1.4fr)_repeat(4,minmax(3.25rem,4.5rem))]";

/**
 * Time of the evaluation steps over the pizzas captured in the window, in the order they run: the average of
 * the whole pipeline on top, each step's share of it, the side-by-side stage counted by its longest branch,
 * what no step measured, and the total at the bottom.
 */
export function StepsCard({ data }: { data: PipelineSection }) {
  const { t, tp } = useLocalization();
  const { rows, accounted } = useMemo(() => stepBreakdown(data.steps), [data.steps]);
  const total = data.totals.latency.pipeline_ms;
  const totalAvg = total?.avg ?? (accounted || null);
  const share = (avg: number | null | undefined) => (totalAvg && avg !== null && avg !== undefined ? avg / totalAvg : null);
  // mixed populations (a step missing on some pizzas) can push the sum past the total: then no remainder
  const rest = total?.avg !== null && total?.avg !== undefined ? total.avg - accounted : null;

  return (
    <SectionCard
      title={t("monitoring.pipeline.steps.title")}
      description={t("monitoring.pipeline.steps.description", { window: t(`monitoring.window.long.${data.window}`) })}
    >
      {rows.length === 0 ? (
        <EmptyBlock>{t("monitoring.pipeline.steps.empty")}</EmptyBlock>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-x-6 gap-y-2 rounded-md bg-muted/30 px-3 py-2.5">
            <div title={t("monitoring.pipeline.steps.totalHint")}>
              <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("monitoring.pipeline.steps.totalAvg")}
              </div>
              <div className="text-2xl font-semibold leading-tight tabular-nums">{formatMs(totalAvg)}</div>
            </div>
            <div className="pb-1">
              <SeriesTotals
                items={[
                  { label: t("monitoring.series.p50"), value: formatMs(total?.p50) },
                  { label: t("monitoring.series.p95"), value: formatMs(total?.p95) },
                  { label: t("monitoring.series.p99"), value: formatMs(total?.p99) },
                  { label: t("monitoring.pipeline.col.max"), value: formatMs(total?.max) },
                  { label: t("monitoring.pipeline.col.n"), value: formatCount(total?.n ?? 0) },
                ]}
              />
            </div>
          </div>

          <div className="flex flex-col text-xs">
            <div className={`${STEP_GRID} hidden border-b pb-1.5 text-[11px] font-medium text-muted-foreground md:grid`}>
              <span>{t("monitoring.pipeline.col.step")}</span>
              <span title={t("monitoring.pipeline.steps.shareHint")}>{t("monitoring.pipeline.steps.share")}</span>
              <span className="text-right">{t("monitoring.pipeline.col.avg")}</span>
              <span className="text-right">{t("monitoring.series.p50")}</span>
              <span className="text-right">{t("monitoring.series.p95")}</span>
              <span className="text-right">{t("monitoring.pipeline.col.max")}</span>
            </div>
            <ol>
              {rows.map((row) =>
                row.kind === "parallel" ? (
                  <StepLine
                    key="parallel"
                    label={t("monitoring.pipeline.steps.parallel")}
                    sub={tp("monitoring.pipeline.steps.branches", row.branches)}
                    hint={t("monitoring.pipeline.steps.parallelHint")}
                    avg={row.avg}
                    share={share(row.avg)}
                    approx
                  />
                ) : (
                  <StepLine
                    key={row.stats.step}
                    label={translateOptional(`monitoring.pipeline.step.${row.stats.step}`) ?? row.stats.step}
                    sub={row.stats.step}
                    mono
                    hint={row.kind === "detail" ? t("monitoring.pipeline.steps.detailHint") : undefined}
                    stats={row.stats}
                    avg={row.stats.avg}
                    share={row.kind === "detail" ? undefined : share(row.stats.avg)}
                    indent={(row.parallel ? 1 : 0) + (row.kind === "detail" ? 1 : 0)}
                    detail={row.kind === "detail"}
                  />
                ),
              )}
              {rest !== null && rest >= 1 && (
                <StepLine
                  label={t("monitoring.pipeline.steps.rest")}
                  hint={t("monitoring.pipeline.steps.restHint")}
                  avg={rest}
                  share={share(rest)}
                  approx
                  muted
                />
              )}
              <StepLine
                label={t("monitoring.pipeline.steps.total")}
                sub="processing_times.total"
                mono
                hint={t("monitoring.pipeline.steps.totalHint")}
                stats={total ?? undefined}
                avg={totalAvg}
                share={totalAvg ? 1 : null}
                total
              />
            </ol>
          </div>
          <p className="text-[11px] text-muted-foreground">{t("monitoring.pipeline.steps.population")}</p>
        </div>
      )}
    </SectionCard>
  );
}

/** One line of the steps list; on phones the columns fold into a label line, a share bar and the stats. */
function StepLine({
  label,
  sub,
  mono = false,
  hint,
  stats,
  avg,
  share,
  indent = 0,
  detail = false,
  approx = false,
  muted = false,
  total = false,
}: {
  label: string;
  sub?: string;
  mono?: boolean;
  hint?: string;
  stats?: HistSummary;
  avg: number | null | undefined;
  /** share of the whole pipeline; undefined = the row adds nothing to it (a detail) */
  share?: number | null;
  indent?: number;
  detail?: boolean;
  /** the time is derived (longest branch, remainder), not measured */
  approx?: boolean;
  muted?: boolean;
  total?: boolean;
}) {
  const { t } = useLocalization();
  const avgText = `${approx ? "≈ " : ""}${formatMs(avg)}`;
  const quiet = detail || muted;
  const pad = indent === 1 ? "pl-3" : indent >= 2 ? "pl-7" : undefined;

  return (
    <li
      title={hint}
      className={cn(
        STEP_GRID,
        total ? "mt-0.5 border-t-2 border-border py-2 font-semibold" : "border-b border-border/50",
        !total && (detail ? "py-1" : "py-1.5"),
      )}
    >
      <span className={cn("min-w-0", indent > 0 && "border-l border-border/70", pad)}>
        <span className={cn("block break-words leading-snug", quiet ? "text-muted-foreground" : "text-foreground", detail && "text-[11px]")}>
          {detail && <span aria-hidden>↳ </span>}
          {label}
        </span>
        {sub && (
          <span className={cn("block break-words text-[10px] font-normal leading-tight text-muted-foreground", mono && "font-mono")}>
            {mono ? sub.split("_").join("_​") : sub}
          </span>
        )}
      </span>
      <span className={cn("text-right font-mono tabular-nums md:hidden", quiet && "text-muted-foreground")}>{avgText}</span>
      {share !== undefined ? (
        <div className={cn("col-span-2 flex items-center gap-2 md:col-span-1", indent > 0 && "max-md:pl-3")}>
          <Meter value={share} className="flex-1" />
          <span className="w-9 shrink-0 text-right text-[11px] font-normal tabular-nums text-muted-foreground">{formatPercent(share)}</span>
        </div>
      ) : (
        <span className="hidden md:block" />
      )}
      <span className={cn("hidden text-right font-mono tabular-nums md:block", quiet && "text-muted-foreground")}>{avgText}</span>
      <span className="hidden text-right font-mono font-normal tabular-nums text-muted-foreground md:block">{formatMs(stats?.p50)}</span>
      <span className={cn("hidden text-right font-mono tabular-nums md:block", quiet && "text-muted-foreground")}>{formatMs(stats?.p95)}</span>
      <span className="hidden text-right font-mono font-normal tabular-nums text-muted-foreground md:block">{formatMs(stats?.max)}</span>
      {stats && !detail && (
        <div className={cn("col-span-2 flex flex-wrap gap-x-3 text-[11px] font-normal text-muted-foreground md:hidden", pad)}>
          <span>
            {t("monitoring.series.p50")} <span className="font-mono tabular-nums text-foreground">{formatMs(stats.p50)}</span>
          </span>
          <span>
            {t("monitoring.series.p95")} <span className="font-mono tabular-nums text-foreground">{formatMs(stats.p95)}</span>
          </span>
          <span>
            {t("monitoring.pipeline.col.max")} <span className="font-mono tabular-nums text-foreground">{formatMs(stats.max)}</span>
          </span>
          <span>
            {t("monitoring.pipeline.col.n")} <span className="font-mono tabular-nums text-foreground">{formatCount(stats.n)}</span>
          </span>
        </div>
      )}
    </li>
  );
}

const CLASS_PREFIX = "evaluation.class@";

/** Evaluations of the backend (finished / failed, by classification) and the pipeline time per bucket. */
export function EvaluationCharts({ query }: { query: UseQueryResult<MetricSeries> }) {
  const { t } = useLocalization();
  const windowText = t(`monitoring.window.long.${query.data?.window ?? "6h"}`);

  const evaluations = useCallback(
    (m: MetricSeries): ChartSeries[] => [
      { key: "evaluation_ok", label: t("monitoring.pipeline.series.evaluationOk"), color: NEUTRAL.mid, type: "bar", stackId: "evaluation", values: counterValues(m, "evaluation.ok") },
      { key: "evaluation_failed", label: t("monitoring.pipeline.series.evaluationFailed"), color: STATUS.critical, type: "bar", stackId: "evaluation", values: counterValues(m, "evaluation.failed") },
    ],
    [t],
  );

  const pipelineTime = useCallback(
    (m: MetricSeries): ChartSeries[] => [
      { key: "pipeline_p95", label: t("monitoring.series.p95"), color: SERIES[0], values: histValues(m, "evaluation.pipeline_ms", "p95") },
      { key: "pipeline_p50", label: t("monitoring.series.p50"), color: SERIES_LIGHT, hidden: true, values: histValues(m, "evaluation.pipeline_ms", "p50") },
    ],
    [t],
  );

  return (
    <div className="grid min-w-0 grid-cols-1 items-start gap-4 xl:grid-cols-2">
      <MetricChartCard
        title={t("monitoring.pipeline.evaluations.title")}
        description={t("monitoring.pipeline.evaluations.description", { window: windowText })}
        query={query}
        build={evaluations}
        format={formatCount}
        integer
        height={180}
        footer={(m) => <EvaluationTotals metrics={m} />}
      />
      <MetricChartCard
        title={t("monitoring.pipeline.pipelineTime.title")}
        description={t("monitoring.pipeline.pipelineTime.description", { window: windowText })}
        query={query}
        build={pipelineTime}
        format={formatMs}
        tickFormat={formatMsTick}
        height={180}
        footer={(m) => {
          const hist = histTotal(m, "evaluation.pipeline_ms");
          return (
            <SeriesTotals
              items={[
                { label: t("monitoring.pipeline.col.n"), value: formatCount(hist?.n ?? 0) },
                { label: t("monitoring.series.p50"), value: formatMs(hist?.p50) },
                { label: t("monitoring.series.p95"), value: formatMs(hist?.p95) },
                { label: t("monitoring.series.p99"), value: formatMs(hist?.p99) },
                { label: t("monitoring.pipeline.col.max"), value: formatMs(hist?.max) },
              ]}
            />
          );
        }}
      />
    </div>
  );
}

function EvaluationTotals({ metrics }: { metrics: MetricSeries }) {
  const { t } = useLocalization();
  const ok = counterTotal(metrics, "evaluation.ok");
  const failed = counterTotal(metrics, "evaluation.failed");
  const classes = Object.entries(metrics.totals.counters)
    .filter(([name, count]) => name.startsWith(CLASS_PREFIX) && count > 0)
    .map(([name, count]) => ({ name: name.slice(CLASS_PREFIX.length), count }))
    .sort((a, b) => b.count - a.count);
  const classTotal = classes.reduce((acc, c) => acc + c.count, 0);

  return (
    <div className="flex flex-col gap-2">
      <SeriesTotals
        items={[
          { label: t("monitoring.pipeline.series.evaluationOk"), value: formatCount(ok) },
          { label: t("monitoring.pipeline.series.evaluationFailed"), value: formatCount(failed), tone: failed > 0 ? "nok" : "neutral" },
          { label: t("monitoring.pipeline.evaluations.failedShare"), value: formatPercent(ok + failed ? failed / (ok + failed) : null, 1), tone: failed > 0 ? "nok" : "neutral" },
        ]}
      />
      {classes.length > 0 && (
        <div>
          <div className="mb-1 text-[11px] font-medium text-muted-foreground">{t("monitoring.pipeline.evaluations.classes")}</div>
          <ul className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
            {classes.map((c) => (
              <li key={c.name} className="grid grid-cols-[minmax(0,1fr)_3rem_2.5rem] items-center gap-2 text-xs">
                <span className="min-w-0 break-words">{c.name}</span>
                <span className="text-right font-mono tabular-nums">{formatCount(c.count)}</span>
                <span className="text-right text-[11px] tabular-nums text-muted-foreground">{formatPercent(c.count / classTotal)}</span>
                <Meter value={c.count / classTotal} className="col-span-3" />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
